import { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import SentimentUtil from '../const/sentiment'
import withStyles from 'beam/dist/styles'
import Tooltip from 'beam/dist/Tooltip'
import classNames from 'classnames'
import { isEmpty, range } from 'lodash'
import { difference } from '../../../Common/Utils'

const styles = theme => ({
  block: {
    cursor: 'pointer',
  },
  svg: {
    display: 'block',
  },
  positive: {
    fill: theme.palette.color.green[500],
  },
  negative: {
    fill: theme.palette.color.red[700],
  },
  neutral: {
    fill: 'transparent',
  },
  channelLine: {
    stroke: theme.palette.grey[400],
  },
})

const renderTooltip = text => (text.length > 64 ? text.substring(0, 64).replace(/\s\S*$/, '') + '...' : text)

class SentimentTape extends Component {
  static propTypes = {
    classes: PropTypes.object,
    theme: PropTypes.object,
    t: PropTypes.func,
    width: PropTypes.number.isRequired,
    graphHeight: PropTypes.number.isRequired,
    totalChannels: PropTypes.number.isRequired,
    heightUnitsPerMs: PropTypes.number.isRequired,
    startTime: PropTypes.number,
    callDuration: PropTypes.number,
    setTime: PropTypes.func,
    texts: PropTypes.array,
  }

  static defaultProps = {
    t: s => s,
    classes: {},
    texts: [],
    startTime: 0,
    callDuration: 0,
    theme: {
      spacing: x => x * 8,
      shape: { borderRadius: 0 },
    },
    setTime: () => {},
  }

  getBarClass = sentiment => {
    const { classes } = this.props
    switch (SentimentUtil.getStateFromScore(sentiment)) {
      case SentimentUtil.state.NEGATIVE:
        return classes.negative
      case SentimentUtil.state.POSITIVE:
        return classes.positive
      default:
        return classes.neutral
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const propsChanged = !isEmpty(difference(nextProps, this.props))
    const stateChanged = !isEmpty(difference(nextState, this.state))
    return propsChanged || stateChanged
  }

  getColumnWidth = () => this.props.theme.spacing(3)
  getX = channel =>
    channel === 1 ? this.props.theme.spacing(1) : 3 * this.props.theme.spacing(1) + this.getColumnWidth()
  getChannelLinePosition = channel => this.getX(channel) + this.getColumnWidth() / 2
  getBarStartTime = start => start + this.props.startTime
  getBarPosition = start => this.getBarStartTime(start) * this.props.heightUnitsPerMs
  getBarHeight = (start, duration) =>
    Math.max(Math.min(duration, this.props.callDuration - this.getBarStartTime(start)) * this.props.heightUnitsPerMs, 2)

  render() {
    const { classes, theme, width, graphHeight, totalChannels, texts, setTime } = this.props

    const {
      shape: { borderRadius },
    } = theme

    return (
      <svg width={width} height={graphHeight} className={classes.svg} data-testid="Sentiment-svg-Graph">
        <Fragment>
          {range(1, totalChannels + 1).map(i => (
            <line
              x1={this.getChannelLinePosition(i)}
              y1={0}
              x2={this.getChannelLinePosition(i)}
              y2={graphHeight}
              className={classes.channelLine}
              key={i}
            />
          ))}
        </Fragment>
        {texts.map(
          ({ start, end, sentiment, channel, text }, i) =>
            SentimentUtil.getStateFromScore(sentiment) !== SentimentUtil.state.NEUTRAL && (
              <Tooltip key={i} disableFocusListener title={renderTooltip(text)}>
                <rect
                  x={this.getX(channel)}
                  y={this.getBarPosition(start)}
                  rx={borderRadius}
                  ry={borderRadius}
                  width={this.getColumnWidth()}
                  height={this.getBarHeight(start, end - start)}
                  className={classNames(classes.block, this.getBarClass(sentiment))}
                  onClick={() => setTime(start)}
                />
              </Tooltip>
            ),
        )}
      </svg>
    )
  }
}

export { SentimentTape as UnstyledSentimentTape }

export default withStyles(styles, { withTheme: true })(SentimentTape)
